import type { AppModule } from "@/lib/modules"
import { isPurchaseSidebarEnabled, isSalesSidebarEnabled, type SidebarFeatures } from "@/lib/modules"

export type SidebarNavId =
  | AppModule
  | "executive-reports"
  | "pos-register"
  | "pos-touch"
  | "pos-shifts"
  | "pos-tables"
  | "pos-kds"
  | "pos-orders"
  | "pos-history"

export type SidebarNavItem = {
  id: SidebarNavId
  href: string
  labelKey: string
  colorKey: AppModule | "pos-register" | "pos-touch" | "pos-shifts"
  isActive: (pathname: string) => boolean
}

export function buildApplicationNavItems(
  hasModule: (module: AppModule) => boolean,
  features?: SidebarFeatures,
): SidebarNavItem[] {
  const items: SidebarNavItem[] = []

  if (hasModule("dashboard")) {
    items.push({
      id: "dashboard",
      href: "/dashboard",
      labelKey: "dashboard",
      colorKey: "dashboard",
      isActive: (pathname) => pathname === "/dashboard",
    })
  }

  if (hasModule("sales") && features?.posRegister !== false) {
    items.push({
      id: "pos-register",
      href: "/dashboard/pos/register",
      labelKey: "posRegister",
      colorKey: "pos-register",
      isActive: (pathname) => pathname === "/dashboard/pos/register",
    })
  }

  if (hasModule("sales") && features?.touchPos === true) {
    items.push({
      id: "pos-touch",
      href: "/dashboard/pos/touch",
      labelKey: "posTouch",
      colorKey: "pos-touch",
      isActive: (pathname) => pathname === "/dashboard/pos/touch" || pathname.startsWith("/dashboard/pos/touch/"),
    })
  }

  if (hasModule("sales")) {
    items.push({
      id: "pos-shifts",
      href: "/dashboard/pos/shifts",
      labelKey: "posShifts",
      colorKey: "pos-shifts",
      isActive: (pathname) => pathname.startsWith("/dashboard/pos/shifts"),
    })
  }

  if (hasModule("sales") && isSalesSidebarEnabled(features)) {
    items.push({
      id: "sales",
      href: "/dashboard/sales",
      labelKey: "sales",
      colorKey: "sales",
      isActive: (pathname) => pathname.startsWith("/dashboard/sales"),
    })
  }

  if (hasModule("stock")) {
    items.push({
      id: "stock",
      href: "/dashboard/stock",
      labelKey: "stock",
      colorKey: "stock",
      isActive: (pathname) => pathname.startsWith("/dashboard/stock") || pathname === "/dashboard/branches",
    })
  }

  if (hasModule("purchasing") && isPurchaseSidebarEnabled(features)) {
    items.push({
      id: "purchasing",
      href: "/dashboard/purchasing",
      labelKey: "purchasing",
      colorKey: "purchasing",
      isActive: (pathname) => pathname.startsWith("/dashboard/purchasing"),
    })
  }

  if (hasModule("reports")) {
    items.push({
      id: "reports",
      href: "/dashboard/reports",
      labelKey: "reports",
      colorKey: "reports",
      isActive: (pathname) =>
        pathname.startsWith("/dashboard/reports") && !pathname.startsWith("/dashboard/reports/executive"),
    })
    items.push({
      id: "executive-reports",
      href: "/dashboard/executive-reports",
      labelKey: "executiveReports",
      colorKey: "reports",
      isActive: (pathname) => pathname.startsWith("/dashboard/executive-reports"),
    })
  }

  if (hasModule("admin")) {
    items.push({
      id: "admin",
      href: "/dashboard/admin",
      labelKey: "admin",
      colorKey: "admin",
      isActive: (pathname) => pathname.startsWith("/dashboard/admin"),
    })
  }

  if (hasModule("restaurant")) {
    items.push(
      {
        id: "pos-tables",
        href: "/dashboard/pos/tables",
        labelKey: "posTables",
        colorKey: "restaurant",
        isActive: (pathname) => pathname.startsWith("/dashboard/pos/tables"),
      },
      {
        id: "pos-kds",
        href: "/dashboard/pos/kds",
        labelKey: "posKds",
        colorKey: "restaurant",
        isActive: (pathname) => pathname.startsWith("/dashboard/pos/kds"),
      },
      {
        id: "pos-orders",
        href: "/dashboard/pos/orders",
        labelKey: "posOrders",
        colorKey: "restaurant",
        isActive: (pathname) => pathname.startsWith("/dashboard/pos/orders"),
      },
      {
        id: "pos-history",
        href: "/dashboard/pos/history",
        labelKey: "posHistory",
        colorKey: "restaurant",
        isActive: (pathname) => pathname.startsWith("/dashboard/pos/history"),
      },
    )
  }

  return items
}
